import req from '@sportninja/common/api/request'
import { useCallback, useMemo, useState } from 'react'

import {
  AnalyticsData,
  DetailedRegistrationData,
  RegistrationTypes,
} from '../types/Registration'

// Stable empty array to prevent unnecessary re-renders
const EMPTY_TAXES_ARRAY: never[] = []

// Separate loading states for different operations
export interface LoadingStates {
  fetch: boolean // fetchData
  info: boolean // fetchDataInfo
  create: boolean // createRegistration, createDivisionRegistration
  update: boolean // updateRegistration, updateDivisionRegistration
  status: boolean // status update operations
}

const initialLoadingStates: LoadingStates = {
  fetch: true,
  info: false,
  create: false,
  update: false,
  status: false,
}

interface TypeObject {
  [key: string]: []
}

export function extractValues(data: TypeObject[]): RegistrationTypes {
  const extracted: any = {}
  data.forEach((item) => {
    const key = Object.keys(item)[0]
    const mappedKey = key.replace('_type', '')
    extracted[mappedKey] = item[key]
  })
  return extracted as RegistrationTypes
}

export type RegistrationData = {
  detailed: DetailedRegistrationData
  analytics: AnalyticsData
}

type CreateRegistrationProps = {
  scheduleId: string
  allowPlayerRegistration: boolean
  registrationType: string
  enableRegistration: boolean
  eventNumber: string
  currency: number
  costPerRegistration: number
  paymentCurrency: number
  paymentMethod: number
  goLive: boolean
  dateRange: {
    starts_at: string
    ends_at: string
  }
  useDarkTheme: boolean
  maxTeamSlots: number
  defaultTaxes: string[]
  bccEmails: string[]
  paymentProvider: number | null
  autoApproveRegistrations: boolean
  enableTeamRegistration: boolean
  enablePlayerRegistration: boolean
  show_waiver: boolean
  waivers: string[]
  addPlayersOutsideOfRegistration: boolean
  player_form_id?: string | null
  team_form_id?: string | null
  header_content?: string | null
  team_confirmation_email_content?: string | null
  player_confirmation_email_content?: string | null
  defer_payment?: boolean
}

type DivisionRegistrationProps = {
  scheduleId: string
  currency: number
  description?: string
  dayOfWeek: number
  divisionGender: number
  maxTeams: number | null
  deposit: number | null
  teamCost: number | null
  divisionAge: number
  maxPlayersPerTeam: number | null
  singlePayment: boolean
  maxTeamRepPerTeam: number | null
  installments: number | null
  installmentStartDate: string | null
  installmentFrequency: string | null
  depositRequired: boolean
  playerInstallments: number | null
  playerInstallmentFrequency: string | null
  playerInstallmentStartDate: string | null
  playerDepositRequired: boolean
  playerCostPerRegistration: number | null
  playerDeposit: number | null
  playerSinglePayment: boolean
  defaultTaxes: string[]
  maxPlayersPerBucket: number | null
  showExistingRegistrationsTeams: boolean
  showExistingRegistrationsPlayers: boolean
  playersOnlyRegisterBuckets: boolean
  deferPayment: boolean
  playerDeferPayment: boolean
  divisionDayOfWeekV2: string | null
  team_confirmation_email_content?: string | null
  player_confirmation_email_content?: string | null
  isVisibleRegistration: boolean
  isVisibleStandings: boolean
  isTeamRegistration: boolean
  isPlayerRegistration: boolean
}

type UpdateTeamRegistrationStatusProps = {
  scheduleId: string
  status: number
  conditions?: string | null
  conditionType?: number | null
  divisionId?: string
  teamRegistrationId: string
  team_registration_id?: string
  refund_all_funds?: boolean
}

type UpdatePlayerRegistrationStatusProps = {
  scheduleId: string
  status: number
  conditions?: string | null
  conditionType?: number | null
  divisionId?: string
  teamRegistrationId: string
  refund_all_funds?: boolean
}

// TODO: ⚠️  SN-6156 – This hook calls the Admin API.
// It should NOT be used on the public registration flow.
// Replace with public endpoint once backend is ready.
export const useRegistrationData = (scheduleId?: string) => {
  const [data, setData] = useState<RegistrationData | null>(null)
  const [types, setTypes] = useState<RegistrationTypes | null>(null)
  const [loadingStates, setLoadingStates] =
    useState<LoadingStates>(initialLoadingStates)
  const [error, setError] = useState<Error | null>(null)
  // Analytics is now derived from data.analytics to avoid state duplication

  // Backward compatibility: derived loading from loadingStates
  const loading = loadingStates.fetch || loadingStates.info

  const isRegistrationEmpty = useMemo(() => {
    return (
      !data?.detailed?.registration?.starts_at ||
      data?.detailed?.divisions?.length === 0
    )
  }, [data])

  const fetchTypes = useCallback(async () => {
    try {
      const response = await req('/types/registration')
      setTypes(extractValues(response))
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching types')
      )
    }
  }, [])

  const uploadRegistrationImage = useCallback(
    async (schedule_id: string, imageData: any) => {
      try {
        await req(`/registration/${schedule_id}/admin/image`, {
          method: 'POST',
          body: imageData,
        })
      } catch (err) {
        throw err instanceof Error
          ? err
          : new Error('An error occurred while uploading registration image')
      }
    },
    []
  )

  const deleteRegistrationImage = useCallback(async (schedule_id: string) => {
    try {
      await req(`/registration/${schedule_id}/admin/image`, {
        method: 'DELETE',
      })
    } catch (err) {
      throw err instanceof Error
        ? err
        : new Error('An error occurred while deleting registration image')
    }
  }, [])

  const createDivisionRegistration = useCallback(
    async (props: DivisionRegistrationProps) => {
      setLoadingStates((prev) => ({ ...prev, create: true }))
      setError(null)
      try {
        const response = await req(
          `/registration/${props.scheduleId}/admin/division`,
          {
            method: 'POST',
            body: JSON.stringify({
              currency: props.currency,
              description: props?.description || '',
              day_of_week: props.dayOfWeek,
              division_gender: props.divisionGender,
              max_teams: props.maxTeams,
              max_players_per_bucket: props.maxPlayersPerBucket,
              deposit: props.deposit,
              team_cost: props.teamCost,
              division_age_v2: props?.divisionAge,
              division_day_of_week_v2: props.divisionDayOfWeekV2,
              max_players_per_team: props.maxPlayersPerTeam,
              single_payment: props.singlePayment,
              max_team_rep_per_team: props.maxTeamRepPerTeam,
              installments: props.installments,
              installment_start_date: props.installmentStartDate,
              installment_frequency: props.installmentFrequency,
              deposit_required: props.depositRequired,
              player_installments: props.playerInstallments,
              player_installment_start_date: props.playerInstallmentStartDate,
              player_installment_frequency: props.playerInstallmentFrequency,
              player_deposit_required: props.playerDepositRequired,
              player_cost: props.playerCostPerRegistration,
              player_deposit: props.playerDeposit,
              player_single_payment: props.playerSinglePayment,
              taxes: props.defaultTaxes?.map((el) => ({ id: el })) || [],
              show_existing_registrations_teams:
                props?.showExistingRegistrationsTeams || false,
              show_existing_registrations_players:
                props?.showExistingRegistrationsPlayers || false,
              players_only_register_buckets:
                props?.playersOnlyRegisterBuckets || false,
              defer_payment: props.deferPayment,
              player_defer_payment: props.playerDeferPayment,
              team_confirmation_email_content:
                props.team_confirmation_email_content,
              player_confirmation_email_content:
                props.player_confirmation_email_content,
              is_visible_registration: props.isVisibleRegistration,
              is_visible_standings: props.isVisibleStandings,
              is_team_registration: props.isTeamRegistration,
              is_player_registration: props.isPlayerRegistration,
            }),
          }
        )
        return response.data
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error(
                'An error occurred while creating division registration'
              )
        )
        throw err
      } finally {
        setLoadingStates((prev) => ({ ...prev, create: false }))
      }
    },
    []
  )

  const updateDivisionRegistration = useCallback(
    async (props: DivisionRegistrationProps) => {
      setLoadingStates((prev) => ({ ...prev, update: true }))
      setError(null)
      try {
        const response = await req(
          `/registration/${props.scheduleId}/admin/division`,
          {
            method: 'PUT',
            body: JSON.stringify({
              currency: props.currency,
              description: props?.description || '',
              day_of_week: props.dayOfWeek,
              division_gender: props.divisionGender,
              max_teams: props.maxTeams,
              max_players_per_bucket: props.maxPlayersPerBucket,
              deposit: props.deposit,
              team_cost: props.teamCost,
              division_age_v2: props.divisionAge,
              division_day_of_week_v2: props.divisionDayOfWeekV2,
              max_players_per_team: props.maxPlayersPerTeam,
              single_payment: props.singlePayment,
              max_team_rep_per_team: props.maxTeamRepPerTeam,
              installments: props.installments,
              installment_start_date: props.installmentStartDate,
              installment_frequency: props.installmentFrequency,
              deposit_required: props.depositRequired,
              player_installments: props.playerInstallments,
              player_installment_start_date: props.playerInstallmentStartDate,
              player_installment_frequency: props.playerInstallmentFrequency,
              player_deposit_required: props.playerDepositRequired,
              player_cost: props.playerCostPerRegistration,
              player_deposit: props.playerDeposit,
              player_single_payment: props.playerSinglePayment,
              taxes: props.defaultTaxes?.map((el) => ({ id: el })) || [],
              show_existing_registrations_teams:
                props?.showExistingRegistrationsTeams || false,
              show_existing_registrations_players:
                props?.showExistingRegistrationsPlayers || false,
              players_only_register_buckets:
                props?.playersOnlyRegisterBuckets || false,
              defer_payment: props.deferPayment,
              player_defer_payment: props.playerDeferPayment,
              team_confirmation_email_content:
                props.team_confirmation_email_content,
              player_confirmation_email_content:
                props.player_confirmation_email_content,
              is_visible_registration: props.isVisibleRegistration,
              is_visible_standings: props.isVisibleStandings,
              is_team_registration: props.isTeamRegistration,
              is_player_registration: props.isPlayerRegistration,
            }),
          }
        )
        return response.data
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error(
                'An error occurred while updating division registration'
              )
        )
        throw err
      } finally {
        setLoadingStates((prev) => ({ ...prev, update: false }))
      }
    },
    []
  )

  const createRegistration = useCallback(
    async (props: CreateRegistrationProps) => {
      setLoadingStates((prev) => ({ ...prev, create: true }))
      setError(null)
      try {
        const response = await req(`/registration/${props.scheduleId}/admin`, {
          method: 'POST',
          body: JSON.stringify({
            auto_approve: props?.autoApproveRegistrations || false,
            is_player_registration: props?.enablePlayerRegistration || false,
            is_team_registration: props?.enableTeamRegistration || false,
            payment_provider_type_id: props?.paymentProvider || null,
            player_registration_allowed:
              props?.allowPlayerRegistration || false,
            registration_type_id: props?.registrationType === 'team' ? 1 : 2,
            is_registration_on: props?.enableRegistration || false,
            official_governing_body_event_number: props.eventNumber,
            currency: props.currency,
            payment_currency: props.paymentCurrency,
            payment_type: props.paymentMethod,
            is_live: props.goLive,
            starts_at: props.dateRange.starts_at,
            ends_at: props.dateRange.ends_at,
            custom_link: 'SportNinja',
            dark_theme: props.useDarkTheme,
            push_notification: false,
            max_teams: props.maxTeamSlots || 0,
            taxes: props.defaultTaxes?.map((el) => ({ id: el })) || [],
            bcc_registrations_to: props.bccEmails
              ? props.bccEmails?.join(',')
              : '',
            show_waiver: props.show_waiver,
            waivers: props.show_waiver
              ? props.waivers?.map((el) => ({ id: el })) || []
              : [],
            can_add_players_from_roster:
              props?.addPlayersOutsideOfRegistration || false,
            show_existing_registrations_teams: true,
            show_existing_registrations_players: true,
            player_form_id: props.player_form_id,
            team_form_id: props.team_form_id,
            header_content: props.header_content,
            team_confirmation_email_content:
              props.team_confirmation_email_content,
            player_confirmation_email_content:
              props.player_confirmation_email_content,
            defer_payment: props.defer_payment,
          }),
        })
        return response.data
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while creating registration')
        )
        throw err
      } finally {
        setLoadingStates((prev) => ({ ...prev, create: false }))
      }
    },
    []
  )

  const updateRegistration = useCallback(
    async (props: CreateRegistrationProps) => {
      setLoadingStates((prev) => ({ ...prev, update: true }))
      setError(null)
      try {
        const response = await req(`/registration/${props.scheduleId}/admin`, {
          method: 'PUT',
          body: JSON.stringify({
            auto_approve: props?.autoApproveRegistrations,
            is_player_registration: props?.enablePlayerRegistration,
            is_team_registration: props?.enableTeamRegistration,
            payment_provider_type_id: props?.paymentProvider || null,
            player_registration_allowed: props?.allowPlayerRegistration,
            registration_type_id: props?.registrationType === 'team' ? 1 : 2,
            is_registration_on: props?.enableRegistration,
            official_governing_body_event_number: props.eventNumber,
            currency: props.currency,
            payment_currency: props.paymentCurrency,
            payment_type: props.paymentMethod,
            is_live: props.goLive,
            starts_at: props.dateRange.starts_at,
            ends_at: props.dateRange.ends_at,
            custom_link: 'SportNinja',
            dark_theme: props.useDarkTheme,
            push_notification: false,
            max_teams: props.maxTeamSlots || 0,
            taxes: props.defaultTaxes?.map((el) => ({ id: el })) || [],
            bcc_registrations_to: props.bccEmails
              ? props.bccEmails?.join(',')
              : '',
            show_waiver: props.show_waiver,
            waivers: props.show_waiver
              ? props.waivers?.map((el) => ({ id: el })) || []
              : [],
            can_add_players_from_roster:
              props?.addPlayersOutsideOfRegistration || false,
            show_existing_registrations_teams: true,
            show_existing_registrations_players: true,
            player_form_id: props.player_form_id,
            team_form_id: props.team_form_id,
            header_content: props.header_content,
            team_confirmation_email_content:
              props.team_confirmation_email_content,
            player_confirmation_email_content:
              props.player_confirmation_email_content,
            defer_payment: props.defer_payment,
          }),
        })
        return response.data
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while updating registration')
        )
        throw err
      } finally {
        setLoadingStates((prev) => ({ ...prev, update: false }))
      }
    },
    []
  )

  const updateTeamRegistrationStatus = useCallback(
    async (props: UpdateTeamRegistrationStatusProps) => {
      setError(null)
      const body = {
        team_registration_id:
          props.teamRegistrationId || props.team_registration_id,
        conditions: props.conditions,
        condition_type_id: props.conditionType,
        status: props.status,
        refund_all_funds: props.refund_all_funds,
      }
      if (props.divisionId) {
        ;(body as any).division_id = props.divisionId
      }

      const response = await req(
        `/registration/${props.scheduleId}/admin/team-status`,
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      )

      return response.data
    },
    []
  )

  const updatePlayerRegistrationStatus = useCallback(
    async (props: UpdatePlayerRegistrationStatusProps) => {
      setError(null)
      try {
        const body = {
          team_registration_id: props.teamRegistrationId,
          status: props.status,
          conditions: props.conditions,
          condition_type_id: props.conditionType,
          refund_all_funds: props.refund_all_funds,
        }
        if (props.divisionId) {
          ;(body as any).division_id = props.divisionId
        }

        const response = await req(
          `/registration/${props.scheduleId}/admin/player-status`,
          {
            method: 'PUT',
            body: JSON.stringify(body),
          }
        )

        return response.data
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error(
                'An error occurred while updating player registration status'
              )
        )
        throw err
      }
    },
    []
  )

  const fetchData = useCallback(
    async (displayLoading = true) => {
      if (!scheduleId) {
        return
      }

      if (displayLoading) {
        setLoadingStates((prev) => ({ ...prev, fetch: true }))
      }
      setError(null)

      try {
        // Run all API calls in parallel for better performance
        const [detailedResponse, infoResponse, analyticsResult] =
          await Promise.all([
            req(`/registration/${scheduleId}/admin/detailed-new`),
            req(`/registration/${scheduleId}/admin/info`),
            // Wrap analytics in a promise that never rejects (non-critical)
            req(`/registration/${scheduleId}/admin/analytics`).catch(
              () => null
            ),
          ])

        detailedResponse.data.registration = infoResponse.data
        detailedResponse.data.available_taxes =
          infoResponse.data.available_taxes
        setData({
          detailed: detailedResponse.data,
          analytics: analyticsResult?.data || ({} as AnalyticsData),
        })
      } catch (err: any) {
        setError(err)
      } finally {
        if (displayLoading) {
          setLoadingStates((prev) => ({ ...prev, fetch: false }))
        }
      }
    },
    [scheduleId]
  )

  const fetchAnalytics = useCallback(async () => {
    if (!scheduleId || !data) {
      return
    }
    try {
      const analyticsResponse = await req(
        `/registration/${scheduleId}/admin/analytics`
      )
      // Update data with new analytics
      setData((prev) =>
        prev
          ? {
              ...prev,
              analytics: analyticsResponse.data || ({} as AnalyticsData),
            }
          : null
      )
    } catch (err: any) {
      // Analytics failure is non-critical, silently ignore
    }
  }, [scheduleId, data])

  /**
   * Fetches fresh division registration data by division ID
   * @param divisionId - The division ID (schedule ID of the division)
   * @returns The division registration data from the API
   */
  const fetchDivisionRegistration = useCallback(async (divisionId: string) => {
    const response = await req(`/registration/${divisionId}/admin/division`)
    return response.data
  }, [])

  const fetchDataInfo = useCallback(
    async (displayLoading = true) => {
      if (!scheduleId) {
        return
      }
      // This should only be called if data is available
      if (!data) {
        return
      }
      if (displayLoading) {
        setLoadingStates((prev) => ({ ...prev, info: true }))
      }
      setError(null)
      try {
        // Run API calls in parallel for better performance
        const [infoResponse, analyticsResult] = await Promise.all([
          req(`/registration/${scheduleId}/admin/info`),
          // Wrap analytics in a promise that never rejects (non-critical)
          req(`/registration/${scheduleId}/admin/analytics`).catch(() => null),
        ])

        // Create new detailed object to avoid direct mutation
        const updatedDetailed = {
          ...data.detailed,
          registration: infoResponse.data,
          available_taxes: infoResponse.data.available_taxes,
        }
        setData({
          detailed: updatedDetailed,
          analytics: analyticsResult?.data || ({} as AnalyticsData),
        })
      } catch (err: any) {
        setError(err)
      } finally {
        if (displayLoading) {
          setLoadingStates((prev) => ({ ...prev, info: false }))
        }
      }
    },
    [scheduleId, data]
  )

  // Memoize availableTaxes to prevent new array reference on each render
  const availableTaxes = useMemo(
    () => data?.detailed?.available_taxes || EMPTY_TAXES_ARRAY,
    [data?.detailed?.available_taxes]
  )

  // Derive analytics from data to avoid state duplication
  const analytics = useMemo(
    () => data?.analytics || ({} as AnalyticsData),
    [data?.analytics]
  )

  // Memoize the entire return object to prevent consumers from re-rendering
  return useMemo(
    () => ({
      data,
      loading,
      loadingStates, // Granular loading states for specific operations
      error,
      refetch: fetchData,
      isRegistrationEmpty,
      types,
      availableTaxes,
      createRegistration,
      updateRegistration,
      uploadRegistrationImage,
      deleteRegistrationImage,
      createDivisionRegistration,
      updateDivisionRegistration,
      updateTeamRegistrationStatus,
      updatePlayerRegistrationStatus,
      fetchTypes,
      fetchAnalytics,
      analytics,
      fetchDataInfo,
      fetchDivisionRegistration,
    }),
    [
      data,
      loading,
      loadingStates,
      error,
      fetchData,
      isRegistrationEmpty,
      types,
      availableTaxes,
      createRegistration,
      updateRegistration,
      uploadRegistrationImage,
      deleteRegistrationImage,
      createDivisionRegistration,
      updateDivisionRegistration,
      updateTeamRegistrationStatus,
      updatePlayerRegistrationStatus,
      fetchTypes,
      fetchAnalytics,
      analytics,
      fetchDataInfo,
      fetchDivisionRegistration,
    ]
  )
}
